import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TaskReviewStatus } from '../../../../enums/supplier-risk-management/task-review-status';
import { SupplierRiskManagementService } from '../../../../services/supplier-risk-management.service';

@Component({
  selector: 'task-review-status',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './task-review-status.component.html',
  styleUrls: ['./task-review-status.component.css'],
})
export class TaskReviewStatusComponent implements OnInit, OnChanges {
  @Input() taskId: number;

  @Input() riskStatus: TaskReviewStatus = TaskReviewStatus.NoRisks;
  @Output() riskStatusChange: EventEmitter<TaskReviewStatus> = new EventEmitter<TaskReviewStatus>();

  constructor(
    private supplierRiskManagementService: SupplierRiskManagementService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadStatusInfo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['riskStatus'] || changes['reviewDate']) {
      this.loadStatusInfo();
    }
  }

  statusText: string = '';
  statusBackgroundColour = 'bg-gray-100';
  statusTextColour = '';
  statusBackgroundHover = '';

  reviewDateText: string = '';

  openForTooLong: boolean = false;
  minutesUntilOpenTooLong: number = 90;

  loadStatusInfo() {
    let timeSinceReviewDate: string = '';

    if (this.reviewDate) {
      const reviewDateObj = new Date(this.reviewDate); // ensure it's a Date
      const diffMs = Date.now() - reviewDateObj.getTime();
      const diffMinutes = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      if (hours > 0 && minutes > 0) {
        timeSinceReviewDate = `${hours} hr${hours > 1 ? 's' : ''} ${minutes} min${
          minutes > 1 ? 's' : ''
        }`;
      } else if (hours > 0) {
        timeSinceReviewDate = `${hours} hr${hours > 1 ? 's' : ''}`;
      } else {
        timeSinceReviewDate = `${minutes} min${minutes > 1 ? 's' : ''}`;
      }
      this.openForTooLong = diffMinutes > this.minutesUntilOpenTooLong;
    }

    switch (this.riskStatus) {
      case TaskReviewStatus.NoRisks:
        this.statusText = 'No Risks';
        return;
      case TaskReviewStatus.Open:
        this.statusText = 'Open';
        this.dropdownText = 'Begin task review';
        if (this.openForTooLong) {
          this.statusBackgroundColour = 'bg-red-100';
          this.statusTextColour = 'text-red-700';
          this.statusBackgroundHover = 'hover:bg-red-200';
        } else {
          this.statusBackgroundColour = 'bg-blue-100';
          this.statusTextColour = 'text-blue-700';
          this.statusBackgroundHover = 'hover:bg-blue-200';
        }

        this.reviewDateText = 'for ' + timeSinceReviewDate;
        return;
      case TaskReviewStatus.InReview:
        this.dropdownText = 'Complete task review';
        this.statusText = 'In Review';
        this.statusBackgroundColour = 'bg-orange-100';
        this.statusTextColour = 'text-orange-500';
        this.statusBackgroundHover = '';
        this.reviewDateText = 'for ' + timeSinceReviewDate;
        return;
      case TaskReviewStatus.Reviewed:
        this.statusText = 'Reviewed';
        this.statusBackgroundColour = 'bg-green-100';
        this.statusTextColour = 'text-green-600';
        this.statusBackgroundHover = '';
        this.reviewDateText = timeSinceReviewDate + ' ago';
        return;
      case TaskReviewStatus.Resolved:
        this.statusText = 'Resolved';
        this.statusBackgroundColour = 'bg-green-200';
        this.statusTextColour = 'text-green-700';
        return;
    }
  }

  protected readonly TaskRiskStatus = TaskReviewStatus;
  protected showDropdown: boolean = false;
  @Input() reviewDate: Date | null;
  protected dropdownText: string;

  toggleDropdown() {
    if (this.riskStatus == TaskReviewStatus.Open || this.riskStatus == TaskReviewStatus.InReview) {
      this.showDropdown = !this.showDropdown;
    }
  }

  async updateReviewStatus() {
    if (this.riskStatus == TaskReviewStatus.Open) {
      await this.supplierRiskManagementService.beginTaskReview(this.taskId);
      this.riskStatus = TaskReviewStatus.InReview;
    } else if (this.riskStatus == TaskReviewStatus.InReview) {
      await this.supplierRiskManagementService.completeTaskReview(this.taskId);
      this.riskStatus = TaskReviewStatus.Reviewed;
    }

    this.reviewDateText = ' for 0 mins';
    this.riskStatusChange.emit(this.riskStatus);
    this.loadStatusInfo();
    this.cdr.markForCheck();
    this.showDropdown = false;
  }
}
